import { eraseAccountMirror, eraseSignedInShares } from "../data/mirror.js";
import { deleteAccount, rememberedUser, signOutForDeletion, type User } from "./auth.js";

export const DELETION_KEY = "trayflux:account-deletion";
export interface AccountDeletion { user: User; phase: "pending" | "confirmed" | "local" }

export function pendingDeletion(): AccountDeletion | undefined {
  const raw = localStorage.getItem(DELETION_KEY);
  if (!raw) return;
  let value: unknown;
  try { value = JSON.parse(raw); } catch { /* Report an explicit recovery error below. */ }
  if (!value || typeof value !== "object" || !("user" in value) || !value.user ||
    typeof value.user !== "object" || !("id" in value.user) || typeof value.user.id !== "string" || !value.user.id ||
    !("email" in value.user) || typeof value.user.email !== "string" || !value.user.email ||
    !("phase" in value) || typeof value.phase !== "string" || !["pending", "confirmed", "local"].includes(value.phase)) {
    throw new Error("Account cleanup information could not be read. Clear this site's data to continue.");
  }
  return value as AccountDeletion;
}

export function saveDeletion(state: AccountDeletion): void {
  // Must survive a reload before the irreversible request is sent. Storage failures are not success.
  localStorage.setItem(DELETION_KEY, JSON.stringify(state));
}

export async function requestDeletion(state: AccountDeletion, stop: () => Promise<void>): Promise<void> {
  saveDeletion(state);
  await stop();
  await deleteAccount(state.user.email, state.user.id);
  state.phase = "confirmed";
  saveDeletion(state);
}

/** Called only after stopping stores. The marker stays until every cleanup step succeeds. */
export async function clearAccountBrowser(state: AccountDeletion): Promise<void> {
  await eraseAccountMirror(state.user.id);
  await eraseSignedInShares();
  const remembered = rememberedUser();
  if (!remembered || remembered.id === state.user.id) localStorage.removeItem("trayflux:user");
  const rawPending = localStorage.getItem("trayflux:pending-sign-in");
  if (rawPending) {
    let pending: unknown;
    try { pending = JSON.parse(rawPending); } catch { /* Invalid presentation state grants no access. */ }
    if (!pending || typeof pending !== "object" || !("email" in pending) || typeof pending.email !== "string" ||
      pending.email.toLowerCase() === state.user.email.toLowerCase()) localStorage.removeItem("trayflux:pending-sign-in");
  }
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith(`trayflux.completed.${state.user.id}.`)) localStorage.removeItem(key);
  }
  // Local data can be erased offline, but a live HttpOnly session could restore it
  // on the next boot. Keep the recovery marker until logout is acknowledged.
  if (state.phase === "local") await signOutForDeletion();
  if ("serviceWorker" in navigator) {
    for (const registration of await navigator.serviceWorker.getRegistrations()) {
      if (registration.scope === `${location.origin}/`) await registration.unregister();
    }
  }
  if ("caches" in globalThis) {
    for (const key of await caches.keys()) {
      // Vite/Workbox owns these app-shell caches. Leave unrelated applications' caches alone.
      if (key.startsWith("workbox-") && key.includes(location.origin)) await caches.delete(key);
    }
  }
  localStorage.removeItem(DELETION_KEY);
}
